/**
 * Admin sidebar – same structure and styling as UserSidebar (green/black theme)
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../../providers';
import { useI18n } from '../../providers';
import { ROUTES } from '../../utils/constants';
import '../../styles/landing.css';

const AdminSidebar = ({ isOpen = true, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthContext();
  const { language } = useI18n();

  const menuItems = [
    { id: 'dashboard', label: language === 'fr' ? 'Tableau de bord' : 'Dashboard', icon: '📊', path: '/admin/dashboard' },
    { id: 'transactions', label: language === 'fr' ? 'Transactions' : 'Transactions', icon: '💳', path: '/admin/transactions' },
    { id: 'users', label: language === 'fr' ? 'Utilisateurs' : 'Users', icon: '👥', path: '/admin/users' },
    { id: 'disputes', label: language === 'fr' ? 'Litiges' : 'Disputes', icon: '⚠️', path: '/admin/disputes' },
    { id: 'finance', label: language === 'fr' ? 'Finance' : 'Finance', icon: '💰', path: '/admin/finance' },
    { id: 'escrows', label: language === 'fr' ? 'Escrows' : 'Escrows', icon: '🔒', path: '/admin/escrows' },
    { id: 'payments', label: language === 'fr' ? 'Paiements' : 'Payments', icon: '💳', path: '/admin/payments' },
    { id: 'notifications', label: language === 'fr' ? 'Notifications' : 'Notifications', icon: '🔔', path: '/admin/notifications' },
    { id: 'analytics', label: language === 'fr' ? 'Analytique' : 'Analytics', icon: '📈', path: '/admin/analytics' },
    { id: 'user-verification', label: language === 'fr' ? 'Vérification' : 'Verification', icon: '🆔', path: '/admin/user-verification' },
    { id: 'profile', label: language === 'fr' ? 'Profil' : 'Profile', icon: '👤', path: '/admin/profile' },
    { id: 'security', label: language === 'fr' ? 'Sécurité' : 'Security', icon: '🛡️', path: '/admin/security' },
    { id: 'settings', label: language === 'fr' ? 'Paramètres' : 'Settings', icon: '⚙️', path: '/admin/settings' },
  ];

  const currentPath = location.pathname;
  const activeItem = menuItems.find((item) => currentPath.startsWith(item.path))?.id || menuItems.find((item) => currentPath.includes(item.id))?.id || 'dashboard';

  const handleNavigate = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  return (
    <>
      {isOpen && (
        <div
          className="user-sidebar-overlay"
          onClick={onClose}
          aria-hidden
        />
      )}

      <aside
        className={`user-sidebar ${isOpen ? 'user-sidebar-open' : ''}`}
        aria-label={language === 'fr' ? 'Navigation admin' : 'Admin navigation'}
      >
        <div className="user-sidebar-inner">
          <div className="user-sidebar-header">
            <span className="flex-1 min-w-0" aria-hidden />
            <button
              type="button"
              onClick={onClose}
              className="user-sidebar-close"
              aria-label={language === 'fr' ? 'Fermer le menu' : 'Close menu'}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Admin strip (same pattern as wallet strip) */}
          <div className="user-sidebar-wallet">
            <div className="user-sidebar-wallet-top">
              <span className="user-sidebar-wallet-label">{language === 'fr' ? 'Zone' : 'Zone'}</span>
              <span className="user-sidebar-wallet-badge user-sidebar-wallet-badge-verified">
                {user?.role === 'super_admin' ? (language === 'fr' ? 'Super Admin' : 'Super Admin') : (language === 'fr' ? 'Admin' : 'Admin')}
              </span>
            </div>
            <div className="user-sidebar-wallet-amount" style={{ fontSize: '0.875rem', opacity: 0.9 }}>
              {language === 'fr' ? 'Sécurisée' : 'Secure'}
            </div>
            <button type="button" onClick={() => handleNavigate('/admin/dashboard')} className="user-sidebar-wallet-btn">
              {language === 'fr' ? 'Tableau de bord' : 'Dashboard'}
            </button>
          </div>

          <nav className="user-sidebar-nav">
            {menuItems.map((item) => {
              const isActive = activeItem === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleNavigate(item.path)}
                  className={`user-sidebar-item ${isActive ? 'user-sidebar-item-active' : ''}`}
                >
                  <span className="user-sidebar-item-icon">{item.icon}</span>
                  <span className="user-sidebar-item-label">{item.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="user-sidebar-actions">
            <button
              type="button"
              onClick={() => handleNavigate('/dashboard')}
              className="user-sidebar-cta"
            >
              <span className="user-sidebar-cta-icon">←</span>
              {language === 'fr' ? 'Tableau utilisateur' : 'User Dashboard'}
            </button>
            <button
              type="button"
              onClick={() => handleNavigate(ROUTES.SYSTEM_STATUS)}
              className="user-sidebar-secondary"
            >
              {language === 'fr' ? 'Statut système' : 'System Status'}
            </button>
          </div>

          <div className="user-sidebar-footer">
            <div className="user-sidebar-secure">
              <span className="user-sidebar-secure-dot" aria-hidden />
              <span className="user-sidebar-secure-text">{language === 'fr' ? 'Sécurisé' : 'Secure'}</span>
            </div>
            <p className="user-sidebar-secure-desc">
              {language === 'fr' ? 'Zone admin protégée' : 'Admin zone protected'}
            </p>
          </div>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;
